import React, { ReactNode } from 'react';
import {
  Progress,
  ResponseErrorPanel,
  Table,
  TableColumn,
} from '@backstage/core-components';
import { useApi } from '@backstage/core-plugin-api';
import {
  CatalogApi,
  catalogApiRef,
  EntityRefLink,
} from '@backstage/plugin-catalog-react';
import { Box, Grid, Paper, Typography, makeStyles } from '@material-ui/core';
import { useAsync } from 'react-use';
import { LABEL_SERVICE_PROVIDER } from '../input/ServiceProvidersOnlyPicker';
import { VIRTUAL_TAG } from '../input/VirtualPicker';

/** One value of a field and how many entities carry it. */
type FacetCount = { value: string; count: number };

/** How many entities of a kind there are, and how many of each type. */
type KindCount = { kind: string; count: number; types: FacetCount[] };

type CatalogStatistics = {
  kinds: KindCount[];
  owners: FacetCount[];
  ownerFields: FacetCount[];
  lifecycles: FacetCount[];
  productTags: FacetCount[];
  managementModes: FacetCount[];
  platformTeams: FacetCount[];
  serviceProviderLabels: FacetCount[];
  teamTags: FacetCount[];
  departmentTags: FacetCount[];
};

/** Stands for the entities of a kind that state no type at all. */
const NO_TYPE = '(no type)';

/** How many owners the owners table lists. */
const TOP_OWNERS = 10;

/**
 * Everything the tab shows, counted by the catalog itself: a facet is a count
 * per value of a field, worked out on the server, so the tab costs a handful
 * of small requests however large the catalog grows - one for the kinds, one
 * per kind for its types, and a few for the rest.
 */
async function loadStatistics(
  catalogApi: CatalogApi,
): Promise<CatalogStatistics> {
  const facets = async (fields: string[], filter?: Record<string, any>) =>
    (await catalogApi.getEntityFacets({ facets: fields, filter })).facets;

  const [overall, software, products, groups, teams, departments] =
    await Promise.all([
      facets(['kind', 'relations.ownedBy', 'spec.owner']),
      facets(['spec.lifecycle'], { kind: ['Component', 'API'] }),
      facets(['metadata.tags', 'spec.managementMode', 'spec.platformTeam'], {
        kind: 'Product',
      }),
      facets([`metadata.labels.${LABEL_SERVICE_PROVIDER}`], { kind: 'Group' }),
      // The tags of teams and departments apart, for the virtual ones of each.
      facets(['metadata.tags'], { kind: 'Group', 'spec.type': 'team' }),
      facets(['metadata.tags'], { kind: 'Group', 'spec.type': 'department' }),
    ]);

  const kinds = await Promise.all(
    overall.kind.map(async ({ value, count }) => {
      const types = (await facets(['spec.type'], { kind: value }))['spec.type'];
      // An entity without a type is in no facet value, so whatever the types
      // leave over of the kind is that.
      const typed = types.reduce((sum, type) => sum + type.count, 0);
      return {
        kind: value,
        count,
        types:
          typed < count
            ? [...types, { value: NO_TYPE, count: count - typed }]
            : types,
      };
    }),
  );

  return {
    kinds,
    owners: overall['relations.ownedBy'],
    ownerFields: overall['spec.owner'],
    lifecycles: software['spec.lifecycle'],
    productTags: products['metadata.tags'],
    managementModes: products['spec.managementMode'],
    platformTeams: products['spec.platformTeam'],
    serviceProviderLabels: groups[`metadata.labels.${LABEL_SERVICE_PROVIDER}`],
    teamTags: teams['metadata.tags'],
    departmentTags: departments['metadata.tags'],
  };
}

/** Facet values are compared the way the catalog compares them. */
const same = (a: string, b: string) =>
  a.toLocaleLowerCase('en-US') === b.toLocaleLowerCase('en-US');

const countOf = (counts: FacetCount[], value: string) =>
  counts
    .filter(it => same(it.value, value))
    .reduce((sum, it) => sum + it.count, 0);

const formatted = (count: number) => count.toLocaleString('en-US');

const byCount = (a: { count: number }, b: { count: number }) =>
  b.count - a.count;

const sumOf = (counts: FacetCount[]) =>
  counts.reduce((sum, it) => sum + it.count, 0);

const useStyles = makeStyles(theme => ({
  tile: {
    padding: theme.spacing(2),
    height: '100%',
  },
  tileValue: {
    fontWeight: theme.typography.fontWeightMedium as number,
  },
  section: {
    marginTop: theme.spacing(3),
  },
}));

/** A number with what it counts, the way a dashboard leads with one. */
const StatTile = (props: { label: string; value: number }) => {
  const classes = useStyles();
  return (
    <Paper variant="outlined" className={classes.tile}>
      <Typography variant="body2" color="textSecondary">
        {props.label}
      </Typography>
      <Typography variant="h4" component="p" className={classes.tileValue}>
        {formatted(props.value)}
      </Typography>
    </Paper>
  );
};

/** A row of a counting table: what is counted, and how often. */
type CountRow = { id: string; label: ReactNode; count: number; share: number };

const countColumns = (what: string): TableColumn<CountRow>[] => [
  { title: what, field: 'id', render: row => row.label },
  {
    title: 'Entities',
    field: 'count',
    type: 'numeric',
    width: '20%',
    render: row => formatted(row.count),
  },
  {
    title: 'Share',
    field: 'share',
    type: 'numeric',
    width: '15%',
    render: row => `${(row.share * 100).toFixed(1)} %`,
  },
];

/** Counts of one field, largest first, each with its share of the total. */
const CountTable = (props: {
  title: string;
  what: string;
  counts: FacetCount[];
  /**
   * What the shares are of - the sum of `counts` unless given. A table that
   * shows only the largest few passes the sum of all of them, or its shares
   * would be of the few and always add up to 100 %.
   */
  total?: number;
  label?: (value: string) => ReactNode;
}) => {
  const { title, what, counts, label = value => value } = props;
  const total = props.total ?? sumOf(counts);
  const rows = [...counts].sort(byCount).map(it => ({
    id: it.value,
    label: label(it.value),
    count: it.count,
    share: total ? it.count / total : 0,
  }));
  return (
    <Table<CountRow>
      title={title}
      options={{ search: false, paging: false, padding: 'dense' }}
      columns={countColumns(what)}
      data={rows}
    />
  );
};

/** A row of the table of every kind and type. */
type KindTypeRow = { id: string; kind: string; type: string; count: number };

const kindTypeColumns: TableColumn<KindTypeRow>[] = [
  { title: 'Kind', field: 'kind' },
  { title: 'Type', field: 'type' },
  {
    title: 'Entities',
    field: 'count',
    type: 'numeric',
    width: '20%',
    render: row => formatted(row.count),
  },
];

/**
 * How large the catalog is and what it is made of: every kind and every type
 * of it, who owns the most, and how products and software are spread.
 *
 * Next to the tabs that report what went wrong, this one is the picture of
 * what is there - and a source that stops delivering shows here first, as a
 * kind or type that suddenly counts less.
 */
export const CatalogStatisticsContent = () => {
  const classes = useStyles();
  const catalogApi = useApi(catalogApiRef);
  const { value, loading, error } = useAsync(
    () => loadStatistics(catalogApi),
    [catalogApi],
  );

  if (loading) {
    return <Progress />;
  }
  if (error) {
    return <ResponseErrorPanel error={error} />;
  }
  if (!value) {
    return null;
  }

  const total = value.kinds.reduce((sum, it) => sum + it.count, 0);
  const kind = (name: string) => value.kinds.find(it => same(it.kind, name));
  const typeOf = (kindName: string, type: string) =>
    countOf(kind(kindName)?.types ?? [], type);

  const kindTypeRows: KindTypeRow[] = [...value.kinds]
    .sort(byCount)
    .flatMap(it =>
      [...it.types].sort(byCount).map(type => ({
        id: `${it.kind}/${type.value}`,
        kind: it.kind,
        type: type.value,
        count: type.count,
      })),
    );

  // The owners come as entity refs, the platform teams as bare vuids.
  const ownerLink = (ref: string) => <EntityRefLink entityRef={ref} />;
  const teamLink = (vuid: string) => (
    <EntityRefLink entityRef={`group:default/${vuid}`} />
  );

  return (
    <>
      <Grid container spacing={2}>
        <Grid item xs={6} md={3}>
          <StatTile label="Entities" value={total} />
        </Grid>
        <Grid item xs={6} md={3}>
          <StatTile label="Kinds" value={value.kinds.length} />
        </Grid>
        <Grid item xs={6} md={3}>
          <StatTile label="Users" value={kind('User')?.count ?? 0} />
        </Grid>
        <Grid item xs={6} md={3}>
          <StatTile label="Departments" value={typeOf('Group', 'department')} />
        </Grid>
        <Grid item xs={6} md={3}>
          <StatTile label="Teams" value={typeOf('Group', 'team')} />
        </Grid>
        <Grid item xs={6} md={3}>
          <StatTile
            label="Service providers"
            value={countOf(value.serviceProviderLabels, 'true')}
          />
        </Grid>
        <Grid item xs={6} md={3}>
          <StatTile
            label="Virtual teams"
            value={countOf(value.teamTags, VIRTUAL_TAG)}
          />
        </Grid>
        <Grid item xs={6} md={3}>
          <StatTile
            label="Virtual departments"
            value={countOf(value.departmentTags, VIRTUAL_TAG)}
          />
        </Grid>
        <Grid item xs={6} md={3}>
          <StatTile
            label="Active products"
            value={countOf(value.productTags, 'active')}
          />
        </Grid>
        <Grid item xs={6} md={3}>
          <StatTile
            label="Archived products"
            value={countOf(value.productTags, 'archived')}
          />
        </Grid>
        {/* `unknown` and `external` are what the providers put in for an
            owner they have none for. */}
        <Grid item xs={6} md={3}>
          <StatTile
            label="Owner unknown"
            value={countOf(value.ownerFields, 'unknown')}
          />
        </Grid>
        <Grid item xs={6} md={3}>
          <StatTile
            label="Owner external"
            value={countOf(value.ownerFields, 'external')}
          />
        </Grid>
      </Grid>

      <Grid container spacing={3} className={classes.section}>
        <Grid item xs={12} md={6}>
          <CountTable
            title="Entities by kind"
            what="Kind"
            counts={value.kinds.map(it => ({
              value: it.kind,
              count: it.count,
            }))}
          />
        </Grid>
        <Grid item xs={12} md={6}>
          <CountTable
            title={`Top ${TOP_OWNERS} owners`}
            what="Owner"
            counts={[...value.owners].sort(byCount).slice(0, TOP_OWNERS)}
            total={sumOf(value.owners)}
            label={ownerLink}
          />
        </Grid>
        <Grid item xs={12}>
          <Table<KindTypeRow>
            title={`Entities by kind and type (${formatted(total)})`}
            options={{ paging: false, padding: 'dense' }}
            columns={kindTypeColumns}
            data={kindTypeRows}
          />
        </Grid>
        <Grid item xs={12} md={6}>
          <CountTable
            title="Products by management mode"
            what="Management mode"
            counts={value.managementModes}
          />
        </Grid>
        <Grid item xs={12} md={6}>
          <CountTable
            title="Products by platform team"
            what="Platform team"
            counts={value.platformTeams}
            label={teamLink}
          />
        </Grid>
        <Grid item xs={12} md={6}>
          <CountTable
            title="Components and APIs by lifecycle"
            what="Lifecycle"
            counts={value.lifecycles}
          />
        </Grid>
      </Grid>
      <Box mt={2}>
        <Typography variant="caption" color="textSecondary">
          Counted by the catalog when this tab was opened. Shares are of the
          entities each table counts.
        </Typography>
      </Box>
    </>
  );
};
